/**
 * This module holds all of the implementations for keeping GraphQL data in sync with Flux and Redux.
 */
import {mapObject, objectFromEntries} from '@benchling/js-utils';
import _ from 'underscore';

import {assert} from 'Asserts';
import {
  BIOENTITY_ALIAS_INFO_LOAD_COMPLETED,
  BOX_SCHEMAS_FETCH_COMPLETED,
  BOX_SCHEMAS_UPDATED_FROM_GRAPHQL,
  CONTAINER_SCHEMAS_FETCH_COMPLETED,
  CONTAINER_SCHEMAS_UPDATED_FROM_GRAPHQL,
  FILE_BROWSER_SOURCES_FETCHED,
  LOCATION_SCHEMAS_FETCH_COMPLETED,
  LOCATION_SCHEMAS_UPDATED_FROM_GRAPHQL,
  PLATE_SCHEMAS_FETCH_COMPLETED,
  PLATE_SCHEMAS_UPDATED_FROM_GRAPHQL,
  SOURCES_UPDATED_FROM_GRAPHQL,
} from 'constants/EventConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {Plate} from 'constants/SampleConstants';
import AliasesForBioentitySyncer from 'graphql-client/sync/syncers/AliasesForBioentitySyncer';
import AppDispatcher from 'lib/Dispatcher';
import {ManagedModelType, ManagedModelTypes} from 'models/ManagedModelDefinitions';
import {modelFetched} from 'models/ManagedModelsReduxActions';
import {ManagedModelSelector} from 'models/reduceManagedModels';
import NavStore from 'nav/NavStore';
import {
  entriesFetchCompleted,
  filesFetchCompleted,
  workflowSummariesFetchCompleted,
} from 'redux-stores/ModelActions';
import {getStore} from 'redux-stores/Store';
import {requestsFetchCompleted} from 'requests/RequestReduxActions';
import {
  requestTaskSchemasFetched,
  requestTaskSchemasUpdatedFromGQL,
} from 'requests/schemas/RequestTaskSchemaReduxActions';
import {requestTasksFetched} from 'requests/tasks/RequestTaskReduxActions';
import {
  fetchAllRequestTemplatesCompleted,
  requestTemplatesUpdatedFromGQL,
} from 'requests/templates/RequestTemplateReduxActions';
import {dispatchAssaySchemasListed, dispatchAssaySchemasUpdatedFromGQL} from 'results/AssaySchemaActions';
import AssayStore from 'results/AssayStore';
import {handleBatchesLoaded} from 'samples/SampleActions';
import SampleSchemaStore from 'samples/SampleSchemaStore';
import {dispatchStorablesLoaded, dispatchStorableSummariesLoaded} from 'samples/StorableActions';
import {
  gridsFromStoreToValidMapOfType,
  gridSummariesFromStoreToValidMapOfType,
} from 'samples/storables/graphql/gridsFromStoreToValidMapOfType';
import StorableStore from 'samples/StorableStore';
import {fetchStarredTagSchemasCompleted} from 'stars/StarsReduxActions';
import {sampleGroupsFetchCompleted} from 'workflows/samples/SampleGroupReduxActions';
import {dispatchStageRunSamplesFetched} from 'workflows/StageRunActions';

import SyncerFactory from './SyncerFactory';
import RequestSampleGroupsRelationshipSyncer from './syncers/RequestSampleGroupsRelationshipSyncer';
import RequestTasksRelationshipSyncer from './syncers/RequestTasksRelationshipSyncer';
import SampleGroupSchemaSyncer from './syncers/SampleGroupSchemaSyncer';
import SchemaFieldSelectorsForRegistrySyncer from './syncers/SchemaFieldSelectorsForRegistrySyncer';
import {SyncDeclarationInterface, SyncInterface, TFragmentName} from './SyncInterface';

type ExtendedSyncInterface = SyncInterface & {
  SchemaFieldSelectorsForRegistryGraphQL: SchemaFieldSelectorsForRegistrySyncer;
};

function getAllManagedModelsByKey<T extends keyof ManagedModelTypes>(
  modelType: T
): {[key: string]: ManagedModelTypes[T]} {
  return new ManagedModelSelector(getStore().getState()).getAllModelsByKey(modelType);
}

const ACCESSIBLE_OR_INACCESSIBLE_SYNCER_DEFINITIONS: SyncDeclarationInterface = {
  BioentitySummaryOrInaccessibleGraphQL: {
    readFromStore: () => {
      const fileSummariesById = getStore().getState().models.fileSummariesById ?? {};
      const invalidIds = getStore().getState().models.inaccessibleFileIds;

      // We can't tell the value of canRequestAccess from the id alone, so must default to true.
      const inaccessibleFilesById = objectFromEntries(
        invalidIds.map((invalidId) => [invalidId, {invalidId, canRequestAccess: true}])
      );

      return {
        current: {...fileSummariesById, ...inaccessibleFilesById},
        equalityRef: {fileSummariesById, invalidIds},
      };
    },
    dispatchAccessibleOrInaccessibleUpdates: (restObjsByID, inaccessibleIds) => {
      getStore().dispatch(filesFetchCompleted(restObjsByID, inaccessibleIds));

      const bioentityAliasInfosById = objectFromEntries(
        Object.values(restObjsByID).map((obj) => [obj.id, obj.bioentityAliasInfos])
      );

      AppDispatcher.dispatch({
        actionType: BIOENTITY_ALIAS_INFO_LOAD_COMPLETED,
        bioentityAliasInfosById,
      });
    },
  },
  EntrySummaryOrInaccessibleGraphQL: {
    readFromStore: () => {
      const entrySummariesById = getStore().getState().models.entrySummariesById ?? {};
      const invalidIds = getStore().getState().models.inaccessibleEntryIds;

      // We can't tell the value of canRequestAccess from the id alone, so must default to true.
      const inaccessibleFilesById = objectFromEntries(
        invalidIds.map((invalidId) => [invalidId, {invalidId, canRequestAccess: true}])
      );

      return {
        current: {...entrySummariesById, ...inaccessibleFilesById},
        equalityRef: {entrySummariesById, invalidIds},
      };
    },
    dispatchAccessibleOrInaccessibleUpdates: (restObjsByID, inaccessibleIds) => {
      getStore().dispatch(entriesFetchCompleted(restObjsByID, inaccessibleIds));
    },
  },
};

const ROOT_QUERY_SYNCER_DEFINITIONS: SyncDeclarationInterface = {
  StarredTagSchemaIdsGraphQL: {
    readCollectionFromStore: () => {
      const starredTagSchemaIds = getStore().getState().models.stars.tagSchemaIds;
      return {current: starredTagSchemaIds ? {starredTagSchemaIds} : null, equalityRef: starredTagSchemaIds};
    },
    dispatchCollectionUpdates: (restResult) => {
      if (restResult.starredTagSchemaIds) {
        getStore().dispatch(fetchStarredTagSchemasCompleted({starred: restResult.starredTagSchemaIds}));
      }
    },
  },
  NavStoreRegistryGraphQL: {
    dispatchUpdates: (restObjsByID) => {
      if (NavStore.sources) {
        AppDispatcher.dispatch({
          actionType: SOURCES_UPDATED_FROM_GRAPHQL,
          sources: Object.values(restObjsByID),
        });
      }
    },
  },
};

const ROOT_COLLECTION_PAIR_SYNCER_DEFINITIONS: SyncDeclarationInterface = {
  AllAssaySchemasGraphQL: {
    stores: [AssayStore],
    readCollectionFromStore: () => ({
      current: AssayStore.assaySchemasById
        ? {
            requestSchemas: AssayStore.getRequestSchemas() ?? [],
            resultSchemas: AssayStore.getResultSchemas() ?? [],
            runSchemas: AssayStore.getRunSchemas() ?? [],
          }
        : null,
      equalityRef: AssayStore.assaySchemasById,
    }),
    dispatchCollectionUpdates: (restResult) => {
      dispatchAssaySchemasListed([
        ...restResult.requestSchemas,
        ...restResult.resultSchemas,
        ...restResult.runSchemas,
      ]);
    },
  },
  AssaySchemaGraphQL: {
    dispatchUpdates: (restObjsByID) => {
      if (AssayStore.assaySchemasById) {
        dispatchAssaySchemasUpdatedFromGQL(Object.values(restObjsByID));
      }
    },
  },
  AllBoxSchemasGraphQL: {
    stores: [SampleSchemaStore],
    readCollectionFromStore: () => ({
      current: SampleSchemaStore.boxSchemas ? {boxSchemas: SampleSchemaStore.boxSchemas} : null,
      equalityRef: SampleSchemaStore.boxSchemas,
    }),
    dispatchCollectionUpdates: (restResult) => {
      AppDispatcher.dispatch({
        actionType: BOX_SCHEMAS_FETCH_COMPLETED,
        boxSchemas: restResult.boxSchemas,
      });
    },
  },
  BoxSchemaGraphQL: {
    dispatchUpdates: (restObjsByID) => {
      if (SampleSchemaStore.boxSchemas) {
        AppDispatcher.dispatch({
          actionType: BOX_SCHEMAS_UPDATED_FROM_GRAPHQL,
          boxSchemas: Object.values(restObjsByID),
        });
      }
    },
  },
  AllContainerSchemasGraphQL: {
    stores: [SampleSchemaStore],
    readCollectionFromStore: () => ({
      current: SampleSchemaStore.containerSchemas
        ? {containerSchemas: SampleSchemaStore.containerSchemas}
        : null,
      equalityRef: SampleSchemaStore.containerSchemas,
    }),
    dispatchCollectionUpdates: (restResult) => {
      AppDispatcher.dispatch({
        actionType: CONTAINER_SCHEMAS_FETCH_COMPLETED,
        containerSchemas: restResult.containerSchemas,
      });
    },
  },
  ContainerSchemaGraphQL: {
    dispatchUpdates: (restObjsByID) => {
      if (SampleSchemaStore.containerSchemas) {
        AppDispatcher.dispatch({
          actionType: CONTAINER_SCHEMAS_UPDATED_FROM_GRAPHQL,
          containerSchemas: Object.values(restObjsByID),
        });
      }
    },
  },
  AllLocationSchemasGraphQL: {
    stores: [SampleSchemaStore],
    readCollectionFromStore: () => ({
      current: SampleSchemaStore.locationSchemas
        ? {locationSchemas: SampleSchemaStore.locationSchemas}
        : null,
      equalityRef: SampleSchemaStore.locationSchemas,
    }),
    dispatchCollectionUpdates: (restResult) => {
      AppDispatcher.dispatch({
        actionType: LOCATION_SCHEMAS_FETCH_COMPLETED,
        locationSchemas: restResult.locationSchemas,
      });
    },
  },
  LocationSchemaGraphQL: {
    dispatchUpdates: (restObjsByID) => {
      if (SampleSchemaStore.locationSchemas) {
        AppDispatcher.dispatch({
          actionType: LOCATION_SCHEMAS_UPDATED_FROM_GRAPHQL,
          locationSchemas: Object.values(restObjsByID),
        });
      }
    },
  },
  AllPlateSchemasGraphQL: {
    stores: [SampleSchemaStore],
    readCollectionFromStore: () => ({
      current: SampleSchemaStore.plateSchemas ? {plateSchemas: SampleSchemaStore.plateSchemas} : null,
      equalityRef: SampleSchemaStore.plateSchemas,
    }),
    dispatchCollectionUpdates: (restResult) => {
      AppDispatcher.dispatch({
        actionType: PLATE_SCHEMAS_FETCH_COMPLETED,
        plateSchemas: restResult.plateSchemas,
      });
    },
  },
  PlateSchemaGraphQL: {
    dispatchUpdates: (restObjsByID) => {
      if (SampleSchemaStore.plateSchemas) {
        AppDispatcher.dispatch({
          actionType: PLATE_SCHEMAS_UPDATED_FROM_GRAPHQL,
          plateSchemas: Object.values(restObjsByID),
        });
      }
    },
  },
  RequestSchemaGraphQL: {
    dispatchUpdates: (restObjsByID) => {
      if (AssayStore.assaySchemasById) {
        dispatchAssaySchemasUpdatedFromGQL(Object.values(restObjsByID));
      }
    },
  },
  AllRequestTaskSchemasGraphQL: {
    readCollectionFromStore: () => {
      const requestTaskSchemasById = getStore().getState().models.requests.schemas.requestTaskSchemasById;
      return {
        current: requestTaskSchemasById ? {requestTaskSchemas: Object.values(requestTaskSchemasById)} : null,
        equalityRef: requestTaskSchemasById,
      };
    },
    dispatchCollectionUpdates: (restResult) => {
      const taskSchemasById = _.indexBy(restResult.requestTaskSchemas, 'id');
      getStore().dispatch(requestTaskSchemasFetched(taskSchemasById));
    },
  },
  AllRequestTemplatesGraphQL: {
    readCollectionFromStore: () => {
      const requestTemplatesById = getStore().getState().models.requests.templates.requestTemplatesById;
      return {
        current: {requestTemplates: Object.values(requestTemplatesById)},
        equalityRef: requestTemplatesById,
      };
    },
    dispatchCollectionUpdates: (restResult) => {
      const templatesById = _.indexBy(restResult.requestTemplates, 'id');
      getStore().dispatch(fetchAllRequestTemplatesCompleted(restResult.requestTemplates, templatesById));
    },
  },
  RequestTemplateGraphQL: {
    readFromStore: () => {
      const requestTemplatesById = getStore().getState().models.requests.templates.requestTemplatesById;
      return {current: requestTemplatesById, equalityRef: requestTemplatesById};
    },
    dispatchUpdates: (restObjsByID) => {
      getStore().dispatch(requestTemplatesUpdatedFromGQL(Object.values(restObjsByID)));
    },
  },
  RequestTaskSchemaGraphQL: {
    dispatchUpdates: (restObjsByID) => {
      if (getStore().getState().models.requests.schemas.requestTaskSchemasById) {
        getStore().dispatch(requestTaskSchemasUpdatedFromGQL(restObjsByID));
      }
    },
  },
  AllSourcesGraphQL: {
    stores: [NavStore],
    readCollectionFromStore: () => ({
      current: NavStore.sources ? {sources: NavStore.sources} : null,
      equalityRef: NavStore.sources,
    }),
    dispatchCollectionUpdates: (restResult) => {
      AppDispatcher.dispatch({
        actionType: FILE_BROWSER_SOURCES_FETCHED,
        sources: restResult.sources,
      });
    },
  },
  NavStoreSourceGraphQL: {
    dispatchUpdates: (restObjsByID) => {
      if (NavStore.sources) {
        AppDispatcher.dispatch({
          actionType: SOURCES_UPDATED_FROM_GRAPHQL,
          sources: Object.values(restObjsByID),
        });
      }
    },
  },
};

const MODEL_SYNCER_DEFINITIONS: SyncDeclarationInterface = {
  BatchGraphQL: {
    stores: [StorableStore],
    readFromStore: () => ({
      current: StorableStore.getBatches(),
      equalityRef: StorableStore.getBatches(),
    }),
    dispatchUpdates: (restObjsByID) => {
      handleBatchesLoaded(Object.values(restObjsByID));
    },
  },
  BoxGraphQL: {
    stores: [StorableStore],
    readFromStore: () => ({
      current: gridsFromStoreToValidMapOfType(StorableStore.grids, ModelTypeConstants.BOX),
      equalityRef: StorableStore.grids,
    }),
    dispatchUpdates: (restObjsByID) => {
      dispatchStorablesLoaded(Object.values(restObjsByID));
    },
  },
  BoxSummaryGraphQL: {
    stores: [StorableStore],
    readFromStore: () => ({
      current: gridSummariesFromStoreToValidMapOfType(StorableStore.gridSummaries, ModelTypeConstants.BOX),
      equalityRef: StorableStore.gridSummaries,
    }),
    dispatchUpdates: (restObjsByID) => {
      dispatchStorableSummariesLoaded(Object.values(restObjsByID));
    },
  },
  ContainerGraphQL: {
    stores: [StorableStore],
    readFromStore: () => ({
      current: StorableStore.containers,
      equalityRef: StorableStore.containers,
    }),
    dispatchUpdates: (restObjsByID) => {
      dispatchStorablesLoaded(Object.values(restObjsByID));
    },
  },
  ContainerSummaryGraphQL: {
    stores: [StorableStore],
    readFromStore: () => ({
      current: StorableStore.containerSummaries,
      equalityRef: StorableStore.containerSummaries,
    }),
    dispatchUpdates: (restObjsByID) => {
      dispatchStorableSummariesLoaded(Object.values(restObjsByID));
    },
  },
  ExecutionSampleGraphQL: {
    readFromStore: () => {
      const stageRunSamples = getStore().getState().models.workflows.fullData.stageRunSamples || {};
      return {
        current: stageRunSamples,
        equalityRef: stageRunSamples,
      };
    },
    dispatchUpdates: (restObjsByID) => {
      dispatchStageRunSamplesFetched({stageRunSamples: Object.values(restObjsByID)});
    },
  },
  FolderWithPermissionsGraphQL: {
    readFromStore: () => ({
      current: getAllManagedModelsByKey(ManagedModelType.FOLDER_BY_ID),
      equalityRef: getStore().getState().models.managed[ManagedModelType.FOLDER_BY_ID],
    }),
    dispatchUpdates: (restObjsByID) => {
      getStore().dispatch(modelFetched(ManagedModelType.FOLDER_BY_ID, restObjsByID));
    },
  },
  LocationGraphQL: {
    stores: [StorableStore],
    readFromStore: () => ({
      current: StorableStore.locations,
      equalityRef: StorableStore.locations,
    }),
    dispatchUpdates: (restObjsByID) => {
      dispatchStorablesLoaded(Object.values(restObjsByID));
    },
  },
  LocationSummaryGraphQL: {
    stores: [StorableStore],
    readFromStore: () => ({
      current: StorableStore.locationSummaries,
      equalityRef: StorableStore.locationSummaries,
    }),
    dispatchUpdates: (restObjsByID) => {
      dispatchStorableSummariesLoaded(Object.values(restObjsByID));
    },
  },
  PlateGraphQL: {
    stores: [StorableStore],
    readFromStore: () => ({
      current: gridsFromStoreToValidMapOfType<Plate>(StorableStore.grids, ModelTypeConstants.PLATE),
      equalityRef: StorableStore.grids,
    }),
    dispatchUpdates: (restObjsByID) => {
      dispatchStorablesLoaded(Object.values(restObjsByID));
    },
  },
  PlateSummaryGraphQL: {
    stores: [StorableStore],
    readFromStore: () => ({
      current: gridSummariesFromStoreToValidMapOfType(StorableStore.gridSummaries, ModelTypeConstants.PLATE),
      equalityRef: StorableStore.gridSummaries,
    }),
    dispatchUpdates: (restObjsByID) => {
      dispatchStorableSummariesLoaded(Object.values(restObjsByID));
    },
  },
  ProtocolSummaryGraphQL: {
    readFromStore: () => ({
      current: getAllManagedModelsByKey(ManagedModelType.PROTOCOL_SUMMARY),
      equalityRef: getStore().getState().models.managed[ManagedModelType.PROTOCOL_SUMMARY],
    }),
    dispatchUpdates: (restObjsByID) => {
      getStore().dispatch(modelFetched(ManagedModelType.PROTOCOL_SUMMARY, restObjsByID));
    },
  },
  RequestGraphQL: {
    readFromStore: () => {
      const requestsById = getStore().getState().models.requests.inputs.requestsById;
      return {
        current: requestsById,
        equalityRef: requestsById,
      };
    },
    dispatchUpdates: (restObjsByID) => {
      getStore().dispatch(requestsFetchCompleted(Object.values(restObjsByID)));
    },
  },
  RequestTaskGraphQL: {
    readFromStore: () => {
      const requestTasksById = getStore().getState().models.requests.inputs.requestTasksById;
      return {
        current: requestTasksById,
        equalityRef: requestTasksById,
      };
    },
    dispatchUpdates: (restObjsByID) => {
      getStore().dispatch(requestTasksFetched(Object.values(restObjsByID)));
    },
  },
  SampleGroupGraphQL: {
    readFromStore: () => {
      const sampleGroupsById = getStore().getState().models.sampleGroups.sampleGroupsById;
      return {
        current: sampleGroupsById,
        equalityRef: sampleGroupsById,
      };
    },
    dispatchUpdates: (restObjsByID) => {
      getStore().dispatch(sampleGroupsFetchCompleted(Object.values(restObjsByID)));
    },
  },
  StageEntrySummaryGraphQL: {
    readFromStore: () => ({
      current: getAllManagedModelsByKey(ManagedModelType.STAGE_ENTRY_SUMMARY),
      equalityRef: getStore().getState().models.managed[ManagedModelType.STAGE_ENTRY_SUMMARY],
    }),
    dispatchUpdates: (restObjsByID) => {
      getStore().dispatch(modelFetched(ManagedModelType.STAGE_ENTRY_SUMMARY, restObjsByID));
    },
  },
  WorkflowSummaryGraphQL: {
    readFromStore: () => {
      const workflows = getStore().getState().models.workflows.summaryData.workflows;
      return {
        current: workflows,
        equalityRef: workflows,
      };
    },
    dispatchUpdates: (restObjsByID) => {
      getStore().dispatch(workflowSummariesFetchCompleted(restObjsByID));
    },
  },
  WorklistSummaryGraphQL: {
    readFromStore: () => ({
      current: getAllManagedModelsByKey(ManagedModelType.WORKLIST_SUMMARIES_BY_ID),
      equalityRef: getStore().getState().models.managed[ManagedModelType.WORKLIST_SUMMARIES_BY_ID],
    }),
    dispatchUpdates: (restObjsByID) => {
      getStore().dispatch(modelFetched(ManagedModelType.WORKLIST_SUMMARIES_BY_ID, restObjsByID));
    },
  },
  WorklistItemsGraphQL: {
    readFromStore: () => ({
      current: _.mapObject(
        getAllManagedModelsByKey(ManagedModelType.WORKLIST_ITEMS_BY_WORKLIST_ID),
        (itemIds, id) => ({
          id,
          itemIds,
        })
      ),
      equalityRef: getStore().getState().models.managed[ManagedModelType.WORKLIST_ITEMS_BY_WORKLIST_ID],
    }),
    dispatchUpdates: (restObjsByID) => {
      getStore().dispatch(
        modelFetched(
          ManagedModelType.WORKLIST_ITEMS_BY_WORKLIST_ID,
          _.mapObject(restObjsByID, (restObj) => restObj.itemIds)
        )
      );
    },
  },
};

const BASE_GENERATED_SYNC_DEFINITIONS = SyncerFactory.getFactorySyncDefinitions({
  ...ACCESSIBLE_OR_INACCESSIBLE_SYNCER_DEFINITIONS,
  ...ROOT_QUERY_SYNCER_DEFINITIONS,
  ...ROOT_COLLECTION_PAIR_SYNCER_DEFINITIONS,
  ...MODEL_SYNCER_DEFINITIONS,
});

// Re-route syncer activity of a "child" fragment type to a "parent" syncer capable of handling a union of
// fragment types.
const PROXIED_SYNCERS: {[key in TFragmentName]?: TFragmentName} = {
  BioentitySummaryGraphQL: 'BioentitySummaryOrInaccessibleGraphQL',
  MixtureSummaryGraphQL: 'BioentitySummaryOrInaccessibleGraphQL',
  MoleculeSummaryGraphQL: 'BioentitySummaryOrInaccessibleGraphQL',
  ProteinSummaryGraphQL: 'BioentitySummaryOrInaccessibleGraphQL',
  EntrySummaryGraphQL: 'EntrySummaryOrInaccessibleGraphQL',
};

function getProxiedSyncDefinitions(): SyncInterface {
  return mapObject(PROXIED_SYNCERS, (value: TFragmentName) => {
    assert(
      BASE_GENERATED_SYNC_DEFINITIONS[value] != null,
      `Tried to proxy to a syncer that does not exist. Looked for ${value}.`
    );

    return BASE_GENERATED_SYNC_DEFINITIONS[value];
  });
}

const SYNC_DEFINITIONS: ExtendedSyncInterface = {
  ...BASE_GENERATED_SYNC_DEFINITIONS,
  ...getProxiedSyncDefinitions(),
  AliasesForBioentityGraphQL: new AliasesForBioentitySyncer(),
  GraphQLRequestSampleGroupsRelationship: new RequestSampleGroupsRelationshipSyncer(),
  GraphQLRequestTasksRelationship: new RequestTasksRelationshipSyncer(),
  SampleGroupSchemaGraphQL: new SampleGroupSchemaSyncer(),
  SchemaFieldSelectorsForRegistryGraphQL: new SchemaFieldSelectorsForRegistrySyncer(),
};

export default SYNC_DEFINITIONS;
